import { faCircleCheck, faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { persistAgreementSignaturesThunk } from 'actions/agreements/AgreementsActions';
import { dogHighFive, heroGirlCombingDog } from 'assets/images';
import clsx from 'clsx';
import { PoweredByCuddles } from 'components/Cuddles/PoweredBy/PoweredByCuddles';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightImageColumn } from 'components/Layout/TwoColumns/RightImageColumn/RightImageColumn';
import { TwoColumnsLayout } from 'components/Layout/TwoColumns/TwoColumnsLayout';
import { Alert } from 'components/UI/Alert/Alert';
import { usePhoneFormat } from 'hooks/usePhoneFormat';
import React from 'react';
import { useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import { generatePath } from 'react-router-dom';
import { getMarketplace } from 'reducers/marketplace/MarketplaceSelectors';
import { dispatch } from 'store';
import { OnlineBookingHeader } from 'components/OnlineBooking/Header/OnlineBookingHeader';
import { getOnlineBookingState } from 'reducers/onlineBooking/OnlineBookingSelectors';
import { getBookingsState } from 'reducers/bookings/BookingsSelectors';
import { Button } from 'components/UI/Button/Button';
import { goToPetParentPortal } from 'utils/petParentPortal';
import { bookingRequiresApproval } from 'utils/serviceUtils';
import { FormPath } from 'routes/Paths';
import classes from './OnlineBookingSuccessStep.module.scss';

export const OnlineBookingSuccessStep: React.FC = () => {
    const { agreementSignatures, bookingItems } = useSelector(getOnlineBookingState);
    const {
        name,
        phone,
        slug,
        onlineBookingPreferences: olbPreferences
    } = useSelector(getMarketplace);

    const { booking } = useSelector(getBookingsState);
    const hasPendingForms = booking?.pendingForms?.hasPending === true;
    const pendingFormsToken = booking?.pendingForms?.token;

    const requiresApproval = bookingRequiresApproval(bookingItems, olbPreferences);

    const formattedPhone = usePhoneFormat(phone || '');

    const [isSavingSignatures, setIsSavingSignatures] = React.useState(true);

    const hasUnsavedSignatures = agreementSignatures.some(signature => !signature.persisted);

    React.useEffect(() => {
        dispatch(persistAgreementSignaturesThunk()).finally(() => setIsSavingSignatures(false));
        // eslint-disable-next-line
    }, []);

    return (
        <TwoColumnsLayout header={<OnlineBookingHeader />}>
            <LeftColumn contentClassName={clsx(classes.content)}>
                <FontAwesomeIcon icon={faCircleCheck} className={classes.icon} />

                <h1 className={clsx('h1', classes.title)}>
                    {requiresApproval ? 'Appointment requested!' : 'Appointment booked!'}
                </h1>

                {requiresApproval ? (
                    <p>
                        Your appointment request is currently pending. We&apos;ll notify you once
                        it&apos;s accepted. If you have any questions contact <b>{name}</b> at{' '}
                        <b>{formattedPhone}</b>
                    </p>
                ) : (
                    <p>
                        Check your inbox for an appointment confirmation. If you have any questions
                        contact <b>{name}</b> at <b>{formattedPhone}</b>
                    </p>
                )}

                {!isSavingSignatures && hasUnsavedSignatures && (
                    <Alert variant="warning" icon={faCircleExclamation} className={classes.alert}>
                        We couldn&apos;t save your agreement signature. <b>{name}</b> will send you
                        the agreement again so you can sign it.
                    </Alert>
                )}

                {hasPendingForms && (
                    <>
                        <Button
                            className={classes.formsAction}
                            disabled={isSavingSignatures}
                            loading={isSavingSignatures}
                            onClick={() => {
                                if (pendingFormsToken) {
                                    dispatch(
                                        push(
                                            generatePath(FormPath, {
                                                slug,
                                                token: pendingFormsToken
                                            })
                                        )
                                    );
                                    return;
                                }

                                goToPetParentPortal(slug);
                            }}
                        >
                            Complete Required Forms
                        </Button>

                        <Button
                            className={clsx(classes.portalAction, classes.portalLink)}
                            variant="underlined-link"
                            disabled={isSavingSignatures}
                            onClick={() => goToPetParentPortal(slug)}
                        >
                            Go to Pet Parent Portal
                        </Button>
                    </>
                )}

                <div>
                    <PoweredByCuddles />
                </div>

                <img src={dogHighFive} alt="Dog high five" className={classes.dogImage} />
            </LeftColumn>

            <RightImageColumn image={olbPreferences.promoImageUrl ?? heroGirlCombingDog} />
        </TwoColumnsLayout>
    );
};
